import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { JobEditor } from "./JobEditor";
import { API, http, authHeaders, formatApiError, fmtDate, CAREERS_TOKEN } from "../../../lib/api";

export const JobsTab = ({ onAuthError }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`${API}/careers/admin/jobs`, { headers: authHeaders(CAREERS_TOKEN) });
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (j) => {
    if (!window.confirm(`Delete ${j.job_code} - ${j.title}? This removes it from the Careers page immediately.`)) return;
    try {
      await http.delete(`${API}/careers/admin/jobs/${j.id}`, { headers: authHeaders(CAREERS_TOKEN) });
      setJobs((p) => p.filter((x) => x.id !== j.id));
      toast.success("Job deleted");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="jobs-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">{jobs.filter((j) => j.is_active).length} active · {jobs.length} total</p>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-medium text-[#0a0c0e] hover:bg-emerald-400" data-testid="jobs-new-btn">
          <Plus className="h-4 w-4" /> New job
        </button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-md border border-white/10">
        <table className="w-full min-w-[820px] text-sm" data-testid="jobs-table">
          <thead className="bg-[#111519] text-left text-xs font-mono uppercase tracking-[0.12em] text-zinc-400">
            <tr>{["Job ID", "Title", "Organisation", "Posted", "Apply by", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-400" /></td></tr>}
            {!loading && jobs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400" data-testid="jobs-empty">No jobs yet. Click "New job" to publish your first opening.</td></tr>}
            {!loading && jobs.map((j) => (
              <tr key={j.id} className="border-t border-white/10" data-testid={`job-row-${j.job_code}`}>
                <td className="px-4 py-3 font-mono text-emerald-300">{j.job_code}</td>
                <td className="px-4 py-3">{j.title}</td>
                <td className="px-4 py-3 text-zinc-300">{j.organisation}</td>
                <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmtDate(j.posted_on)}</td>
                <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmtDate(j.apply_by)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-0.5 text-xs ${j.is_active ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-zinc-400"}`}>{j.is_active ? "Active" : "Closed"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Link to={`/careers/${encodeURIComponent(j.job_code)}`} target="_blank" className="rounded p-2 text-zinc-300 hover:text-emerald-300" aria-label="View" data-testid={`job-view-${j.job_code}`}><ExternalLink className="h-4 w-4" /></Link>
                    <button onClick={() => setEditing(j)} className="rounded p-2 text-zinc-300 hover:text-emerald-300" aria-label="Edit" data-testid={`job-edit-${j.job_code}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(j)} className="rounded p-2 text-zinc-300 hover:text-red-400" aria-label="Delete" data-testid={`job-delete-${j.job_code}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <JobEditor
          key={editing.id ?? "new"}
          job={editing.id ? editing : null}
          open
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};
